import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import omit from 'lodash/omit';
import { useContext, useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import {
  ApiError,
  MilitaryBranchEnum,
  MilitaryStatusEnum,
  ProviderCredentialingIdentification,
  ProviderService,
} from '../../../api';
import { Card } from '../../../app/components/Card';
import { FileUploader } from '../../../app/components/FileUploader';
import { FormFeedback } from '../../../app/components/FormFeedback';
import { FormFieldVisibility, FormLabel } from '../../../app/components/FormLabel';
import { FormTextField } from '../../../app/components/FormTextField';
import { SelectController } from '../../../app/components/SelectController';
import { ServerErrorsAlert } from '../../../app/components/ServerErrorsAlert';
import { UnsavedChangesFormPrompt } from '../../../app/components/UnsavedChangesFormPrompt';
import { FormSubmitContext } from '../../../app/contexts/FormSubmitContext';
import { useDocumentTitle } from '../../../app/hooks/use-document-title';
import { providerQueryKeys } from '../../../app/query-keys-factories/providers';
import { userQueryKeys } from '../../../app/query-keys-factories/user';
import { handleApiError } from '../../../app/utils/errorhandling';
import { generateShowFieldErrors, generateZEnumFromEnum } from '../../../utils/forms';
import { ProviderProfileCreateOutletContext } from '../../Registration/ProviderProfileCreate';
import { ProviderFormContainer } from './ProviderFormContainer';

const validationSchema = z.object({
  ssn: z.union([z.null(), z.string().length(0), z.string()]),
  birth_date: z.union([
    z.string().length(0),
    z.coerce.date().transform((val: Date) => {
      return new Date(val.valueOf() + val.getTimezoneOffset() * 60 * 1000);
    }),
  ]),
  birth_city: z.union([z.string().length(0), z.string()]),
  birth_state: z.union([z.string().length(0), z.string()]),
  birth_country: z.union([z.string().length(0), z.string()]),
  military_status: z.union([
    z.null(),
    z.string().length(0),
    generateZEnumFromEnum(MilitaryStatusEnum),
  ]),
  military_branch: z.union([
    z.null(),
    z.string().length(0),
    generateZEnumFromEnum(MilitaryBranchEnum),
  ]),
  dd214: z
    .union([
      z.string().url(), // valid URL for uploaded file
      z.string().length(0), // empty string
      z.null(), // null value
    ])
    .optional(), // field itself is optional
  drivers_license: z
    .union([
      z.string().url(), // valid URL for uploaded file
      z.string().length(0), // empty string
      z.null(), // null value
    ])
    .optional(), // field itself is optional
});

export const ProviderIdentificationForm = () => {
  useDocumentTitle('Provider Identification');
  const { formRef, onSuccess } = useOutletContext<ProviderProfileCreateOutletContext>() ?? {};
  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const [showGlobalServerErrors, setShowGlobalServerErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState<ApiError>();
  const [showServerFieldErrors, setShowServerFieldErrors] = useState<{
    npi?: boolean;
    ssn?: boolean;
    birth_date?: boolean;
    birth_city?: boolean;
    birth_state?: boolean;
    birth_country?: boolean;
    military_branch?: boolean;
    military_status?: boolean;
    dd214?: boolean;
    drivers_license?: boolean;
  }>({});
  const { data: provider } = useQuery({
    queryKey: providerQueryKeys.detailCredentialingIdentification(),
    queryFn: () => ProviderService.providerCurrentCredentialingIdentificationRetrieve(),
  });

  const {
    clearErrors,
    control,
    handleSubmit,
    register,
    reset,
    formState: { isDirty, isSubmitSuccessful, errors },
    setValue,
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  useEffect(() => {
    reset(provider);
  }, [provider]); // eslint-disable-line react-hooks/exhaustive-deps

  const queryClient = useQueryClient();
  const mutationProviderCurrentCredentialingPersonalUpdate = useMutation({
    mutationFn: async (data: ProviderCredentialingIdentification) => {
      await ProviderService.providerCurrentCredentialingIdentificationUpdate({ requestBody: data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(providerQueryKeys.all());
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.detail('current'),
      });
    },
  });

  function handleClearErrorsAlert() {
    setShowGlobalServerErrors(false);
  }

  const handleSubmitClick = () => {
    clearErrors();
    setShowGlobalServerErrors(false);
    setShowServerFieldErrors({});
  };

  const onSubmit = async (data) => {
    try {
      await mutationProviderCurrentCredentialingPersonalUpdate.mutateAsync({
        ...data,
        birth_date: data.birth_date ? format(data.birth_date, 'yyyy-MM-dd') : null,
        ssn: data.ssn ? data.ssn : null,
      });
      toast.success('Provider Personal Data Updated Successfully');
      onSuccess?.();
    } catch (errors) {
      if (errors.status === 400) {
        const fieldErrors = omit(errors.body, ['non_field_errors']);
        setServerErrors(errors);
        setShowServerFieldErrors(generateShowFieldErrors(fieldErrors));
        setShowGlobalServerErrors(true);
      }
      handleApiError(errors);
    }
  };

  return (
    <Card className="p-4">
      <h2 className="border-bottom pb-2 mb-4">Identification</h2>
      <Form noValidate onSubmit={handleSubmit(onSubmit)} ref={formRef}>
        <FormTextField
          name="ssn"
          label="SSN"
          type="password"
          serverErrors={serverErrors}
          showServerFieldErrors={showServerFieldErrors}
          setShowServerFieldErrors={setShowServerFieldErrors}
          errors={errors}
          register={register}
          visibility={FormFieldVisibility.PRIVATE}
        />
        <Form.Group controlId="drivers_license">
          <FormLabel visibility={FormFieldVisibility.CONNECTED}>Driver&apos;s License</FormLabel>
          <FileUploader
            name="drivers_license"
            className="mb-3"
            control={control}
            isClearable
            dest="providers_documents"
            isInvalid={!!errors.drivers_license || !!showServerFieldErrors.drivers_license}
            onChange={() => {
              setShowServerFieldErrors(omit(showServerFieldErrors, ['drivers_license']));
            }}
            onRemove={() => {
              setValue('drivers_license', null, { shouldValidate: true });
            }}
          />
          <FormFeedback
            clientError={errors.drivers_license}
            serverErrors={
              showServerFieldErrors.drivers_license ? serverErrors?.body?.drivers_license : null
            }
          />
        </Form.Group>
        <Row>
          <Col xs={6}>
            <FormTextField
              name="birth_date"
              label="Birth Date"
              type="date"
              serverErrors={serverErrors}
              showServerFieldErrors={showServerFieldErrors}
              setShowServerFieldErrors={setShowServerFieldErrors}
              errors={errors}
              register={register}
              visibility={FormFieldVisibility.CONNECTED}
            />
          </Col>
          <Col xs={6}>
            <FormTextField
              name="birth_city"
              label="Birth City"
              serverErrors={serverErrors}
              showServerFieldErrors={showServerFieldErrors}
              setShowServerFieldErrors={setShowServerFieldErrors}
              errors={errors}
              register={register}
              visibility={FormFieldVisibility.CONNECTED}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={6}>
            <FormTextField
              name="birth_state"
              label="Birth State"
              serverErrors={serverErrors}
              showServerFieldErrors={showServerFieldErrors}
              setShowServerFieldErrors={setShowServerFieldErrors}
              errors={errors}
              register={register}
              visibility={FormFieldVisibility.CONNECTED}
            />
          </Col>
          <Col xs={6}>
            <FormTextField
              name="birth_country"
              label="Birth Country"
              serverErrors={serverErrors}
              showServerFieldErrors={showServerFieldErrors}
              setShowServerFieldErrors={setShowServerFieldErrors}
              errors={errors}
              register={register}
              visibility={FormFieldVisibility.CONNECTED}
            />
          </Col>
        </Row>
        <Row className="mb-2">
          <Col xs={6}>
            <FormLabel visibility={FormFieldVisibility.CONNECTED}>Military Branch</FormLabel>
            <SelectController
              name="military_branch"
              control={control}
              options={Object.entries(window.MILITARY_BRANCH).map(([value, label]) => ({
                value: value,
                label: label,
              }))}
              isMulti={false}
              placeholder=""
              value={
                Object.entries(window.MILITARY_BRANCH)
                  .map(([value, label]) => ({ value, label }))
                  .find((option) => option.value === provider?.military_branch) || null
              }
              isInvalid={!!errors.military_branch || !!showServerFieldErrors.military_branch}
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              onChange={(newValue) => {
                setShowServerFieldErrors((prev) => omit(prev, 'military_branch'));
              }}
            />
            <FormFeedback
              clientError={errors.military_branch}
              serverErrors={
                showServerFieldErrors.military_branch ? serverErrors?.body?.military_branch : null
              }
            />
          </Col>
          <Col xs={6}>
            <FormLabel visibility={FormFieldVisibility.CONNECTED}>Military Status</FormLabel>
            <SelectController
              name="military_status"
              control={control}
              options={Object.entries(window.MILITARY_STATUS).map(([value, label]) => ({
                value: value,
                label: label,
              }))}
              isMulti={false}
              placeholder=""
              value={
                Object.entries(window.MILITARY_STATUS)
                  .map(([value, label]) => ({ value, label }))
                  .find((option) => option.value === provider?.military_status) || null
              }
              isInvalid={!!errors.military_status || !!showServerFieldErrors.military_status}
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              onChange={(newValue) => {
                setShowServerFieldErrors((prev) => omit(prev, 'military_status'));
              }}
            />
            <FormFeedback
              clientError={errors.military_status}
              serverErrors={
                showServerFieldErrors.military_status ? serverErrors?.body?.military_status : null
              }
            />
          </Col>
        </Row>
        <Form.Group controlId="dd214">
          <FormLabel visibility={FormFieldVisibility.CONNECTED}>Military DD214</FormLabel>
          <FileUploader
            name="dd214"
            className="mb-3"
            control={control}
            isClearable
            dest="providers_documents"
            isInvalid={!!errors.dd214 || !!showServerFieldErrors.dd214}
            onChange={() => {
              setShowServerFieldErrors(omit(showServerFieldErrors, ['dd214']));
            }}
            onRemove={() => {
              setValue('dd214', null, { shouldValidate: true });
            }}
          />
          <FormFeedback
            clientError={errors.dd214}
            serverErrors={showServerFieldErrors.dd214 ? serverErrors?.body?.dd214 : null}
          />
        </Form.Group>
        <ServerErrorsAlert
          errors={serverErrors}
          showErrors={showGlobalServerErrors}
          clearErrors={handleClearErrorsAlert}
        />
        <Row>
          <Col md={12}>
            <Button
              className="float-end"
              variant="primary"
              type="submit"
              onClick={() => handleSubmitClick()}
              disabled={
                mutationProviderCurrentCredentialingPersonalUpdate.isLoading || !isSubmitEnabled
              }
            >
              Save Changes
            </Button>
          </Col>
        </Row>
      </Form>
      <UnsavedChangesFormPrompt hasUnsavedChanges={isDirty && !isSubmitSuccessful} />
    </Card>
  );
};

export const ProviderIdentification = () => {
  return (
    <ProviderFormContainer>
      <ProviderIdentificationForm />
    </ProviderFormContainer>
  );
};
